import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { LOGO_SRC } from "@/constants/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/school-courses", label: "School Courses" },
  { to: "/competitive-exams", label: "Competitive Exams" },
  { to: "/government-courses", label: "Government Courses" },
  { to: "/languages", label: "Languages" },
  { to: "/it-courses", label: "IT Courses" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-[0_8px_30px_-15px_rgba(78,52,46,.25)] border-b border-brand-line/70"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img src={LOGO_SRC} alt="AM Group of Education" className="h-12 sm:h-14 w-auto object-contain group-hover:scale-105 transition-transform" />
          <div className="leading-tight hidden sm:block">
            <div className="display-serif text-lg font-bold text-brand-dark">AM Group</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-brand-light">of Education</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `nav-link text-sm font-medium text-brand-ink hover:text-brand-primary transition-colors whitespace-nowrap ${isActive ? "active text-brand-primary" : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:block">
          <Link to="/contact" className="btn-primary !px-5 !py-2.5 text-xs whitespace-nowrap" data-testid="nav-enroll-btn">
            Enroll Now <i className="fa-solid fa-arrow-right" />
          </Link>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="xl:hidden w-11 h-11 rounded-lg border border-brand-line bg-white text-brand-dark"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          <i className={`fa-solid ${open ? "fa-xmark" : "fa-bars"}`} />
        </button>
      </div>

      <div className={`xl:hidden overflow-hidden transition-[max-height] duration-500 ${open ? "max-h-[720px]" : "max-h-0"}`}>
        <div className="px-4 pb-6 pt-2 bg-white/95 backdrop-blur border-t border-brand-line">
          <ul className="flex flex-col">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center justify-between py-4 border-b border-brand-line/60 text-brand-ink text-base ${isActive ? "text-brand-primary font-semibold" : ""}`
                  }
                >
                  <span>{l.label}</span>
                  <i className="fa-solid fa-chevron-right text-xs text-brand-mute" />
                </NavLink>
              </li>
            ))}
          </ul>
          <Link to="/contact" className="btn-primary mt-4 w-full" data-testid="mobile-nav-enroll-btn">
            Enroll Now <i className="fa-solid fa-arrow-right" />
          </Link>
        </div>
      </div>
    </header>
  );
}
